import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useChain } from '../chainState'
import { TaskItem } from './TaskItem'

export const ChainTaskPanel = ({ simState, pendingCompletion, onContinue, canManualAdvance, onManualAdvance }) => {
  const [chainState, dispatch] = useChain()
  const { title, description, simulations, chainComplete, activeIndex, reviewIndex } = chainState
  const isLastSim = activeIndex === simulations.length - 1
  const [expandedSims, setExpandedSims] = useState(new Set())

  const toggleExpanded = index => {
    setExpandedSims(prev => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  const simStatusIcons = {
    completed: 'fa-regular fa-circle-check text-success',
    active: 'fa-regular fa-circle-dot text-primary',
    pending: 'fa-regular fa-circle text-muted',
  }

  return (
    <div className="simulation-task-panel">
      {title && <h2 className="simulation-task-panel-title">{title}</h2>}
      {description && <p className="simulation-task-panel-description">{description}</p>}

      {simulations.map((sim, index) => {
        const isActive = sim.status === 'active'
        const isCompleted = sim.status === 'completed'
        const isExpanded = isActive || expandedSims.has(index)
        const iconClass = simStatusIcons[sim.status] || simStatusIcons.pending
        const isReviewing = reviewIndex === index

        // Get tasks to display
        let tasks = null
        if (isActive && simState) {
          tasks = simState.tasks
        } else if (isCompleted && isExpanded && sim.completedTasks) {
          tasks = sim.completedTasks
        }

        const canToggle = isCompleted && sim.completedTasks && sim.completedTasks.length > 0

        return (
          <div key={index} className={`chain-sim-group ${sim.status}${isReviewing ? ' reviewing' : ''}`}>
            <div
              className="chain-sim-header"
              onClick={canToggle ? () => toggleExpanded(index) : undefined}
              style={canToggle ? { cursor: 'pointer' } : undefined}
              role={canToggle ? 'button' : undefined}
              aria-expanded={canToggle ? isExpanded : undefined}
            >
              <i className={iconClass} aria-hidden="true" />
              <span>{sim.title || `Simulation ${index + 1}`}</span>
              {canToggle && (
                <i
                  className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} ms-auto text-muted`}
                  style={{ fontSize: '0.75rem', marginLeft: 'auto' }}
                  aria-hidden="true"
                />
              )}
            </div>

            {isExpanded && tasks && tasks.length > 0 && (
              <div className="chain-sim-tasks">
                <div className="simulation-task-list" role="list" aria-label={`${sim.title || 'Simulation'} tasks`}>
                  {tasks.map((task, taskIndex) => (
                    <TaskItem key={task.id} task={task} index={taskIndex} />
                  ))}
                </div>
                {isActive && pendingCompletion && (
                  <div className="chain-sim-complete-gate" role="status">
                    <h4><i className="fa-regular fa-circle-check" aria-hidden="true" /> All tasks complete!</h4>
                    {sim.completionMessage && <p>{sim.completionMessage}</p>}
                    <button className="btn btn-primary" onClick={onContinue}>
                      {isLastSim ? 'Finish' : 'Continue'}
                    </button>
                  </div>
                )}
                {isActive && !pendingCompletion && canManualAdvance && (
                  <div className="chain-sim-advance-anyway">
                    <button className="btn btn-sm btn-outline-secondary" onClick={onManualAdvance}>
                      {isLastSim ? 'Finish anyway' : 'Continue anyway'}
                    </button>
                  </div>
                )}
                {isCompleted && !chainComplete && (
                  <div className="chain-sim-review-action">
                    {isReviewing ? (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => dispatch({ type: 'SET_REVIEW_INDEX', payload: null })}
                      >
                        <i className="fa-solid fa-arrow-left" aria-hidden="true" /> Back to Current
                      </button>
                    ) : sim.stateSnapshot ? (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => dispatch({ type: 'SET_REVIEW_INDEX', payload: index })}
                      >
                        <i className="fa-regular fa-eye" aria-hidden="true" /> Review
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

ChainTaskPanel.propTypes = {
  simState: PropTypes.shape({
    tasks: PropTypes.array,
    allComplete: PropTypes.bool,
  }),
  pendingCompletion: PropTypes.bool,
  onContinue: PropTypes.func,
  canManualAdvance: PropTypes.bool,
  onManualAdvance: PropTypes.func,
}
